import { useCallback, useMemo } from "react";
import { asNumber } from "../../lib/kernelFormat.js";
import { CanvasSurface, KernelEmpty, resolveCssColor, colorForKey, fillStipplePath } from "./shared.jsx";

export const kind = "chord";

// Circular flow matrix. Nodes sit on a ring, ribbons connect (source,
// target) pairs with width proportional to value. Source and target
// share the same node space, so a name that appears as both ends of
// different flows shows up once. The ring slice for each node is
// sized by total in+out volume passing through it.
export function Component({ spec, data, fillHeight = false }) {
    const model = useMemo(() => buildModel(spec, data), [spec, data]);
    const height = Math.max(220, Math.min(500, Number(spec.height) || 320));

    const draw = useCallback((ctx, { width, height: h, root }) => {
        if (!model) return;
        const { nodes, flows } = model;
        if (!nodes.length) return;
        const cx = width / 2;
        const cy = h / 2;
        const r = Math.min(width, h) / 2 - 30;
        const ringThickness = 10;
        const muted = resolveCssColor(root, "var(--plot-ink-soft)") || "#888";
        const ink = resolveCssColor(root, "var(--ink)") || "#fff";
        const bg = resolveCssColor(root, "var(--plot-bg)") || "#1a1a1a";

        // arc slices
        const totalWeight = nodes.reduce((s, n) => s + n.weight, 0) || 1;
        const gap = (Math.PI * 2) * 0.005; // tiny gap between slices
        let a = -Math.PI / 2;
        const arcs = nodes.map((node) => {
            const sweep = ((node.weight / totalWeight) * (Math.PI * 2)) - gap;
            const start = a;
            const end = a + sweep;
            a = end + gap;
            return { ...node, start, end };
        });
        const arcByName = new Map(arcs.map((x) => [x.name, x]));

        // sub-allocate each arc into per-flow segments so each ribbon
        // has its own anchor wedge on the ring (proportional to its share).
        const segments = new Map(); // flowId → {source: {a0, a1}, target: {a0, a1}}
        for (const arc of arcs) {
            // outflows then inflows
            const outflows = flows.filter((f) => f.source === arc.name);
            const inflows = flows.filter((f) => f.target === arc.name);
            const items = [
                ...outflows.map((f) => ({ flow: f, side: "source", weight: f.value })),
                ...inflows.map((f) => ({ flow: f, side: "target", weight: f.value })),
            ];
            const itemTotal = items.reduce((s, it) => s + it.weight, 0) || 1;
            let cur = arc.start;
            const span = arc.end - arc.start;
            for (const it of items) {
                const w = (it.weight / itemTotal) * span;
                const a0 = cur;
                const a1 = cur + w;
                cur = a1;
                if (!segments.has(it.flow.id)) segments.set(it.flow.id, {});
                segments.get(it.flow.id)[it.side] = { a0, a1 };
            }
        }

        // draw ribbons — stippled fill so texture matches SurfaceChart
        for (const flow of flows) {
            const seg = segments.get(flow.id);
            if (!seg || !seg.source || !seg.target) continue;
            const c = resolveCssColor(root, flow.color);
            const inner = r - ringThickness - 1;
            const traceRibbon = () => {
                ctx.beginPath();
                ribbonPath(ctx, cx, cy, inner, seg.source, seg.target);
            };
            const bbox = { left: cx - inner, top: cy - inner, right: cx + inner, bottom: cy + inner };
            traceRibbon();
            fillStipplePath(ctx, bbox, c, { density: 0.6 });
            traceRibbon();
            ctx.strokeStyle = withAlpha(c, 0.55);
            ctx.lineWidth = 0.5;
            ctx.stroke();
        }

        // draw arcs + labels on top
        ctx.lineWidth = ringThickness;
        for (const arc of arcs) {
            const c = resolveCssColor(root, arc.color);
            ctx.beginPath();
            ctx.arc(cx, cy, r - ringThickness / 2, arc.start, arc.end);
            ctx.strokeStyle = c;
            ctx.stroke();

            const mid = (arc.start + arc.end) / 2;
            const lx = cx + Math.cos(mid) * (r + 6);
            const ly = cy + Math.sin(mid) * (r + 6);
            ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
            ctx.fillStyle = ink;
            ctx.textBaseline = "middle";
            ctx.textAlign = Math.cos(mid) >= 0 ? "left" : "right";
            ctx.fillText(truncate(String(arc.name), 12), lx, ly);
        }

        // small background fill behind labels (skip — keep clean)
        void muted; void bg;
    }, [model]);

    if (!model || !model.flows.length) {
        return <KernelEmpty message={spec.empty || "no flows"} />;
    }

    return (
        <div className={`rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]${fillHeight ? " flex h-full min-h-0 flex-col" : ""}`}>
            <CanvasSurface
                draw={draw}
                deps={[model]}
                height={fillHeight ? null : height}
                className={`w-full bg-[color:var(--plot-bg)] rounded-[2px] border border-[color:var(--line-soft)]${fillHeight ? " flex-1 min-h-0" : ""}`}
            />
        </div>
    );
}

function ribbonPath(ctx, cx, cy, r, source, target) {
    const sA = source.a0;
    const sB = source.a1;
    const tA = target.a0;
    const tB = target.a1;
    const s0 = { x: cx + Math.cos(sA) * r, y: cy + Math.sin(sA) * r };
    const s1 = { x: cx + Math.cos(sB) * r, y: cy + Math.sin(sB) * r };
    const t0 = { x: cx + Math.cos(tA) * r, y: cy + Math.sin(tA) * r };
    const t1 = { x: cx + Math.cos(tB) * r, y: cy + Math.sin(tB) * r };
    // source arc outward
    ctx.moveTo(s0.x, s0.y);
    ctx.arc(cx, cy, r, sA, sB);
    // quadratic to target start
    ctx.quadraticCurveTo(cx, cy, t0.x, t0.y);
    // target arc
    ctx.arc(cx, cy, r, tA, tB);
    // quadratic back
    ctx.quadraticCurveTo(cx, cy, s0.x, s0.y);
    ctx.closePath();
    void s1; void t1;
}

function buildModel(spec, data) {
    const arr = Array.isArray(data) ? data : [];
    const nodes = new Map();
    const flows = [];
    for (let i = 0; i < arr.length; i++) {
        const row = arr[i];
        if (!row) continue;
        const s = row[spec.sourceField];
        const t = row[spec.targetField];
        const v = asNumber(row[spec.valueField]) ?? 0;
        if (s == null || t == null || v <= 0) continue;
        const sName = String(s);
        const tName = String(t);
        if (!nodes.has(sName)) nodes.set(sName, { name: sName, weight: 0, color: spec.color || colorForKey(sName) });
        if (!nodes.has(tName)) nodes.set(tName, { name: tName, weight: 0, color: spec.color || colorForKey(tName) });
        nodes.get(sName).weight += v;
        nodes.get(tName).weight += v;
        flows.push({
            id: `f${i}`,
            source: sName,
            target: tName,
            value: v,
            color: colorForKey(sName),
        });
    }
    return { nodes: [...nodes.values()], flows };
}

function truncate(s, n) {
    if (s.length > n) return s.slice(0, n - 1) + "…";
    return s;
}

function withAlpha(color, alpha) {
    if (!color) return color;
    if (color.startsWith("rgba(")) {
        return color.replace(/rgba\(([^)]+)\)/, (m, inner) => {
            const parts = inner.split(",").map((s) => s.trim());
            parts[3] = String(alpha);
            return `rgba(${parts.join(", ")})`;
        });
    }
    if (color.startsWith("rgb(")) {
        return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
    }
    if (color.startsWith("#")) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
    }
    return color;
}
